import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Activity, BookOpen, Bookmark, NotebookPen, CalendarClock, Trash2, Crown, Users } from "lucide-react";
import { Nav, Footer } from "./Nav";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { BALANCE_KEYS, MAAT_LAWS } from "../data/mock";
import { VideoStudio } from "./VideoStudio";

export function Hub() {
  const { user, loading, logout, openAuth } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState({ mental: 50, physical: 50, emotional: 50, spiritual: 50, social: 50 });
  const [saved, setSaved] = useState([]);
  const [progress, setProgress] = useState([]);
  const [journal, setJournal] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [jLaw, setJLaw] = useState(MAAT_LAWS[0].name);
  const [jEntry, setJEntry] = useState("");
  const [rTitle, setRTitle] = useState("");
  const [rKind, setRKind] = useState("practice");
  const [rWhen, setRWhen] = useState("");
  const [ownerAssets, setOwnerAssets] = useState(null);
  const [bookings, setBookings] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, s, p, j, r] = await Promise.all([
        api.get("/balance"), api.get("/journey"), api.get("/progress"), api.get("/journal"), api.get("/reminders"),
      ]);
      setBalance(b.data); setSaved(s.data); setProgress(p.data); setJournal(j.data); setReminders(r.data);
    } catch {}
    try {
      if (user?.is_admin) { const a = await api.get("/owner/assets"); setOwnerAssets(a.data.groups); }
    } catch {}
    try {
      if (user?.is_admin) { const bk = await api.get("/owner/bookings"); setBookings(bk.data); }
    } catch {}
  }, [user]);

  const downloadOwner = async (name, title) => {
    try {
      const res = await api.get(`/owner/download/${name}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${title}.docx`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download that file."); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { openAuth("login"); navigate("/", { replace: true }); return; }
    load();
  }, [user, loading, load, navigate, openAuth]);

  if (!user) return null;
  const avg = Math.round(BALANCE_KEYS.reduce((s, k) => s + (balance[k.key] || 0), 0) / BALANCE_KEYS.length);
  const todayLaw = MAAT_LAWS[new Date().getDate() % MAAT_LAWS.length];

  const saveBalance = async () => { await api.post("/balance", balance); toast.success("Balance updated"); };
  const addJournal = async (e) => { e.preventDefault(); if (!jEntry.trim()) return; await api.post("/journal", { law: jLaw, entry: jEntry }); setJEntry(""); toast.success("Reflection saved"); load(); };
  const addReminder = async (e) => { e.preventDefault(); if (!rTitle.trim()) return; await api.post("/reminders", { title: rTitle, kind: rKind, when: rWhen }); setRTitle(""); setRWhen(""); toast.success("Reminder added"); load(); };
  const delReminder = async (id) => { await api.delete(`/reminders/${id}`); load(); };
  const removeSaved = async (id) => { await api.delete(`/journey/${id}`); setSaved((p) => p.filter((x) => x.item_id !== id)); };

  return (
    <>
      <Nav />
      <section className="hub-hero">
        <div className="wrap">
          <div className="hub-top">
            <div>
              <span className="eyebrow">MY WELLBEING HUB</span>
              <h1 style={{ fontSize: "clamp(2rem,4vw,3rem)", margin: ".2rem 0" }}>Welcome back, {user.name?.split(" ")[0]}.</h1>
              <span className="member-pill"><Crown size={14} /> {user.membership === "ely_plus" ? "ELY Plus member" : "Free member"}
                {user.membership !== "ely_plus" && <Link to="/" className="text-btn" style={{ marginLeft: ".4rem" }}>Upgrade</Link>}</span>
            </div>
            <button className="btn btn-soft" onClick={() => { logout(); navigate("/"); }}>Log out</button>
          </div>
        </div>
      </section>

      <div className="wrap">
        <div className="maat-mode">
          <div className="mm-glyph">☥</div>
          <div className="mm-body">
            <span className="eyebrow" style={{ color: "var(--gold)" }}>MAAT MODE · TODAY'S LAW</span>
            <h3 style={{ margin: ".2rem 0" }}>Law {todayLaw.n}: {todayLaw.name} — {todayLaw.theme}</h3>
            <p className="muted" style={{ margin: 0 }}>{todayLaw.connection}</p>
          </div>
          <button className="btn btn-gold" onClick={() => { setJLaw(todayLaw.name); document.getElementById("maat-journal")?.scrollIntoView({ behavior: "smooth" }); }}>Reflect now</button>
        </div>

        <div className="maat-mode" style={{ marginTop: "1rem" }} data-testid="study-certify-panel">
          <div className="mm-glyph">🎓</div>
          <div className="mm-body">
            <span className="eyebrow" style={{ color: "var(--gold)" }}>STUDY &amp; CERTIFY</span>
            <h3 style={{ margin: ".2rem 0" }}>Your training courses &amp; certificates</h3>
            <p className="muted" style={{ margin: 0 }}>Access your manuals, take the certification test and download your certificate.</p>
          </div>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            <Link className="btn btn-gold" to="/study/nlp_practitioner" data-testid="study-link-nlp">NLP Practitioner</Link>
            <Link className="btn btn-soft" to="/study/nlp_master_practitioner" data-testid="study-link-nlp-master">NLP Master</Link>
            <Link className="btn btn-soft" to="/study/hypnotherapy_practitioner">Hypnotherapy</Link>
            <Link className="btn btn-soft" to="/study/timespace_practitioner">Time-Space</Link>
            <Link className="btn btn-soft" to="/study/reiki">Reiki</Link>
            <Link className="btn btn-soft" to="/study/reiki_master">Reiki Master</Link>
            <Link className="btn btn-soft" to="/study/eft">EFT</Link>
          </div>
        </div>
        <div className="hub-grid">
          {/* Balance */}
          <section className="hub-card balance">
            <h3><Activity size={18} /> Cosmic Proportion</h3>
            {BALANCE_KEYS.map((k) => (
              <div className="brow" key={k.key}>
                <span className="blabel">{k.label}</span>
                <input type="range" min={0} max={100} value={balance[k.key]} onChange={(e) => setBalance({ ...balance, [k.key]: Number(e.target.value) })} />
                <span className="bval">{balance[k.key]}</span>
              </div>
            ))}
            <div className="cosmic">
              <span className="ring" style={{ ["--pct"]: `${avg}%` }}><i>{avg}</i></span>
              <div><strong>Your balance is {avg}%</strong><small className="empty">Adjust the sliders as you grow, then save.</small></div>
              <button className="btn btn-primary" style={{ marginLeft: "auto" }} onClick={saveBalance}>Save</button>
            </div>
          </section>

          {/* In progress */}
          <section className="hub-card">
            <h3><BookOpen size={18} /> In progress <span className="cnt">{progress.length}</span></h3>
            {progress.length ? progress.map((x) => (
              <div className="hrow" key={x.item_id} style={{ display: "block" }}>
                <strong>{x.title}</strong>
                <div className="bar"><span style={{ width: `${x.percent}%` }} /></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <small className="empty">{(x.status || "").replace("_", " ")} · {x.percent}%</small>
                  <button className="text-btn" onClick={() => navigate(`/course/${x.item_id}`)}>Resume →</button>
                </div>
              </div>
            )) : <p className="empty">Purchased courses appear here. Browse the <Link className="text-btn" to="/courses">Courses</Link>.</p>}
          </section>

          {/* Wishlist */}
          <section className="hub-card">
            <h3><Bookmark size={18} /> My wishlist <span className="cnt">{saved.length}</span></h3>
            {saved.length ? saved.map((x) => (
              <div className="hrow" key={x.item_id}>
                <div><strong>{x.title}</strong><small>{x.section}</small></div>
                <button className="del" onClick={() => removeSaved(x.item_id)}><Trash2 size={16} /></button>
              </div>
            )) : <p className="empty">Tap the heart on any item to save it here.</p>}
          </section>

          {/* Reminders */}
          <section className="hub-card">
            <h3><CalendarClock size={18} /> Reminders & appointments <span className="cnt">{reminders.length}</span></h3>
            {reminders.map((x) => (
              <div className="hrow" key={x.id}>
                <div><strong>{x.title}</strong><small>{x.kind}{x.when ? ` · ${x.when}` : ""}</small></div>
                <button className="del" onClick={() => delReminder(x.id)}><Trash2 size={16} /></button>
              </div>
            ))}
            <form className="inline-form" onSubmit={addReminder}>
              <input placeholder="e.g. Evening meditation" value={rTitle} onChange={(e) => setRTitle(e.target.value)} />
              <select value={rKind} onChange={(e) => setRKind(e.target.value)}><option value="practice">Practice</option><option value="herb">Herb</option><option value="appointment">Appointment</option></select>
              <input placeholder="When (e.g. 9pm daily)" value={rWhen} onChange={(e) => setRWhen(e.target.value)} />
              <button className="btn btn-primary" type="submit">Add</button>
            </form>
          </section>

          {/* Journal */}
          <section className="hub-card" id="maat-journal" style={{ gridColumn: "1 / -1" }}>
            <h3><NotebookPen size={18} /> Maat Journal <span className="cnt">{journal.length}</span></h3>
            <form className="inline-form" onSubmit={addJournal} style={{ marginTop: 0, marginBottom: "1rem" }}>
              <select value={jLaw} onChange={(e) => setJLaw(e.target.value)} style={{ flex: "0 0 160px" }}>
                {MAAT_LAWS.map((l) => <option key={l.n} value={l.name}>{l.name} — {l.theme}</option>)}
              </select>
              <textarea placeholder="How did you live this law today?" value={jEntry} onChange={(e) => setJEntry(e.target.value)} style={{ minWidth: 220 }} />
              <button className="btn btn-primary" type="submit">Save reflection</button>
            </form>
            {journal.length ? journal.map((x) => (
              <div className="hrow" key={x.id} style={{ display: "block" }}>
                <span className="pill">{x.law}</span>
                <p style={{ margin: ".4rem 0 0" }}>{x.entry}</p>
              </div>
            )) : <p className="empty">Your reflections on the 11 Laws will appear here.</p>}
          </section>

          {user.is_admin && bookings && (
            <section className="hub-card" id="bookings" data-testid="owner-bookings" style={{ gridColumn: "1 / -1" }}>
              <h3><Users size={18} /> Bookings <span className="cnt">{bookings.count}</span></h3>
              {bookings.count ? (
                <div style={{ overflowX: "auto", marginTop: ".6rem" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".86rem" }}>
                    <thead>
                      <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                        <th style={{ padding: ".5rem .6rem" }}>Name</th>
                        <th style={{ padding: ".5rem .6rem" }}>Email</th>
                        <th style={{ padding: ".5rem .6rem" }}>Course</th>
                        <th style={{ padding: ".5rem .6rem" }}>Plan</th>
                        <th style={{ padding: ".5rem .6rem" }}>Amount</th>
                        <th style={{ padding: ".5rem .6rem" }}>Via</th>
                        <th style={{ padding: ".5rem .6rem" }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.bookings.map((b, i) => (
                        <tr key={i} data-testid={`booking-row-${i}`} style={{ borderTop: "1px solid var(--line)" }}>
                          <td style={{ padding: ".5rem .6rem", color: "#fff" }}>{b.name}</td>
                          <td style={{ padding: ".5rem .6rem" }}>{b.email}</td>
                          <td style={{ padding: ".5rem .6rem" }}>{b.title}</td>
                          <td style={{ padding: ".5rem .6rem" }}>{b.plan === "installment" ? "6-month" : b.plan === "full" ? "Full" : "—"}</td>
                          <td style={{ padding: ".5rem .6rem" }}>{b.amount ? `${b.currency} ${Number(b.amount).toLocaleString()}` : "—"}{b.plan === "installment" ? "/mo" : ""}</td>
                          <td style={{ padding: ".5rem .6rem", textTransform: "capitalize" }}>{b.provider}</td>
                          <td style={{ padding: ".5rem .6rem", whiteSpace: "nowrap" }}>{b.date ? new Date(b.date).toLocaleDateString("en-GB") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="empty" style={{ marginTop: 0 }}>No bookings yet. You'll get an email the moment someone enrols, and they'll appear here.</p>}
            </section>
          )}

          {user.is_admin && ownerAssets && (
            <section className="hub-card" id="marketing-studio" data-testid="marketing-studio" style={{ gridColumn: "1 / -1" }}>
              <h3><Crown size={18} /> Marketing Studio <span className="cnt">owner</span></h3>
              <p className="empty" style={{ marginTop: 0 }}>Your NLP sales pages, HeyGen video-sales-letter scripts and private reference — Word documents, owner-only.</p>
              {Object.entries(ownerAssets).map(([group, items]) => items.length ? (
                <div key={group} style={{ marginTop: ".8rem" }}>
                  <span className="pill">{group}</span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginTop: ".5rem" }}>
                    {items.map((it) => (
                      <button key={it.name} className="btn btn-gold-outline" data-testid={`owner-dl-${it.name}`}
                        onClick={() => downloadOwner(it.name, it.title)} style={{ fontSize: ".82rem" }}>
                        <BookOpen size={14} /> {it.title}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null)}
            </section>
          )}

          {user.is_admin && <VideoStudio />}
        </div>
      </div>
      <Footer />
    </>
  );
}
